import type { MutableRefObject } from "react";
import { ArrowResetRegular, WarningRegular, ZoomInRegular, ZoomOutRegular } from "@fluentui/react-icons";
import { LOADING_MESSAGES, PDF_CONFIG, ZOOM_LEVEL_OPTIONS } from "../../../constants";
import { PdfViewer } from "./PdfViewer";
import styles from "./DetailView.module.css";

interface CompareViewProps {
  readonly isWordOperationActive: boolean;
  readonly isLoadingPdf: boolean;
  readonly pdfError: string;
  readonly pdfLoaded: boolean;
  readonly totalPages: number;
  readonly zoomLevel: number;
  readonly canvasRefs: MutableRefObject<(HTMLCanvasElement | null)[]>;
  readonly onZoomIn: () => void;
  readonly onZoomOut: () => void;
  readonly onResetZoom: () => void;
  readonly onZoomChange: (level: number) => void;
  readonly onRetryPdf: () => void;
  readonly isLoadingDocx: boolean;
  readonly docxError: string;
  readonly docxHtml: string;
  readonly isDocxLoaded: boolean;
  readonly onRetryDocx: () => void;
  readonly docxZoomLevel: number;
  readonly onDocxZoomIn: () => void;
  readonly onDocxZoomOut: () => void;
  readonly onDocxResetZoom: () => void;
  readonly onDocxZoomChange: (level: number) => void;
}

/** Side-by-side review pane: original input PDF on the left, converted output on the right. */
export function CompareView({
  isWordOperationActive,
  isLoadingPdf,
  pdfError,
  pdfLoaded,
  totalPages,
  zoomLevel,
  canvasRefs,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  onZoomChange,
  onRetryPdf,
  isLoadingDocx,
  docxError,
  docxHtml,
  isDocxLoaded,
  onRetryDocx,
  docxZoomLevel,
  onDocxZoomIn,
  onDocxZoomOut,
  onDocxResetZoom,
  onDocxZoomChange,
}: CompareViewProps) {
  return (
    <div className={styles.compareContainer}>
      <div className={styles.comparePane}>
        <div className={styles.comparePaneHeader}>
          <span className={styles.comparePaneTitle}>Original</span>
        </div>
        <div className={styles.comparePaneBody}>
          <PdfViewer
            isWordOperationActive={isWordOperationActive}
            isLoadingPdf={isLoadingPdf}
            pdfError={pdfError}
            pdfLoaded={pdfLoaded}
            totalPages={totalPages}
            zoomLevel={zoomLevel}
            canvasRefs={canvasRefs}
            onZoomIn={onZoomIn}
            onZoomOut={onZoomOut}
            onResetZoom={onResetZoom}
            onZoomChange={onZoomChange}
            onRetry={onRetryPdf}
          />
        </div>
      </div>

      <div className={styles.compareDivider} aria-hidden="true" />

      <div className={styles.comparePane}>
        <div className={styles.comparePaneHeader}>
          <span className={styles.comparePaneTitle}>Converted</span>
        </div>
        <div className={styles.comparePaneBody}>
          {isLoadingDocx && (
            <div className={styles.loadingState}>
              <span className={styles.spinnerLarge} aria-hidden="true" />
              <p className={styles.loadingText}>{LOADING_MESSAGES.DOCX}</p>
            </div>
          )}

          {!isLoadingDocx && docxError && (
            <div className={styles.emptyState}>
              <WarningRegular className={styles.emptyIconError} aria-hidden="true" />
              <h3 className={styles.emptyTitle}>Error Loading Document</h3>
              <p className={styles.emptyMessage}>{docxError}</p>
              <button type="button" className={styles.btn} onClick={onRetryDocx}>
                Retry
              </button>
            </div>
          )}

          {!isLoadingDocx && !docxError && isDocxLoaded && (
            <>
              <div className={styles.docxZoomViewport}>
                <div
                  className={styles.docxZoomScaler}
                  style={{ transform: `scale(${docxZoomLevel})`, transformOrigin: "top left" }}
                >
                  <iframe title="Converted document preview" className={styles.docxFrame} sandbox="" srcDoc={docxHtml} />
                </div>
              </div>
              <div className={styles.pdfZoomControls}>
                <button
                  type="button"
                  className={styles.zoomBtn}
                  onClick={onDocxZoomOut}
                  disabled={docxZoomLevel <= PDF_CONFIG.MIN_ZOOM}
                  title="Zoom Out"
                  aria-label="Zoom out converted document"
                >
                  <ZoomOutRegular />
                </button>
                <select
                  className={styles.zoomSelect}
                  value={Math.round(docxZoomLevel * 100)}
                  onChange={(e) => onDocxZoomChange(Number(e.target.value) / 100)}
                  aria-label="Zoom level for converted document"
                >
                  {ZOOM_LEVEL_OPTIONS.map((pct) => (
                    <option key={pct} value={pct}>
                      {pct}%
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className={styles.zoomBtn}
                  onClick={onDocxZoomIn}
                  disabled={docxZoomLevel >= PDF_CONFIG.MAX_ZOOM}
                  title="Zoom In"
                  aria-label="Zoom in converted document"
                >
                  <ZoomInRegular />
                </button>
                <button
                  type="button"
                  className={styles.zoomBtn}
                  onClick={onDocxResetZoom}
                  title="Reset Zoom"
                  aria-label="Reset converted document zoom to 100%"
                >
                  <ArrowResetRegular />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
